import { Descriptions, Divider, Flex, Table, Typography } from 'antd';
import styled from 'styled-components';

import { useCollectionStore } from '@stores/useCollectionStore';
import { formatNumber } from '@utils/helpers';
import { formatArea, formatLength, formatAreasWithSharedUnit } from '@utils/measurementSystem';
import { hasStemEstimates, getStemEstimateRows, STEM_ESTIMATE_LEVELS } from '@utils/stemEstimates';
import { objectLabel } from '../utils/transformUtils';

const { Text } = Typography;

// Compact, single-line per-class table: a slightly smaller font and nowrap keep
// metric labels (e.g. "Est. Stems (Medium)") and values on one line; wider
// columns overflow into the Table's horizontal scroll rather than wrapping.
const CompactTable = styled(Table)`
	.ant-table {
		font-size: 0.75rem;
	}
	.ant-table-cell {
		white-space: nowrap;
	}
`;

/**
 * Renders a hexagon `{ stats, detections }` pair — used for both a single
 * selected cell and the whole-collection summary, since the summary aggregator
 * produces the same shape. The stem rows reuse the shared `stemEstimates`
 * helpers so the wording/units match the model-based hexagon UI exactly.
 *
 * @param {object} props
 * @param {object} props.stats - flat cell/summary stats (see hexagonCellStats)
 * @param {object} props.detections - per-class stats map
 */
const HexagonStats = ({ stats, detections }) => {
	const objects = useCollectionStore(s => s.objects);
	const [areaNumber, areaUnit] = formatArea(stats.area);

	const topItems = [
		{ key: 'count', label: 'Total objects', children: formatNumber(stats.count) },
		{ key: 'area', label: 'Area', children: `${areaNumber} ${areaUnit}` },
	];
	if (stats.mean_height != null) {
		const [heightNumber, heightUnit] = formatLength(stats.mean_height);
		topItems.push({
			key: 'meanHeight',
			label: 'Mean height',
			children: `${heightNumber} ${heightUnit}`,
		});
	}

	const showStems = hasStemEstimates(stats.stems_low, stats.stems_medium, stats.stems_high);
	const stemRows = showStems
		? getStemEstimateRows(stats.stems_low, stats.stems_medium, stats.stems_high, {
				perHaLow: stats.stems_per_ha_low,
				perHaMedium: stats.stems_per_ha_medium,
				perHaHigh: stats.stems_per_ha_high,
			})
		: [];

	// Per-class breakdown, transposed: metrics as rows, one column per class.
	// This reads better than a wide row-per-class table in the narrow sidebar and
	// leaves room for the per-class stem rows.
	const classKeys = Object.keys(detections ?? {});
	const perClass = getValue => Object.fromEntries(classKeys.map(key => [key, getValue(key)]));

	// Area is formatted with one shared unit across classes (chosen from the
	// largest value), so the row compares cleanly instead of mixing m² and ha.
	const sharedArea = formatAreasWithSharedUnit(
		classKeys.map(key => detections[key]?.sum_object_area)
	);
	const areaByKey = Object.fromEntries(
		classKeys.map((key, i) => [
			key,
			detections[key]?.sum_object_area != null
				? `${sharedArea.values[i]} ${sharedArea.unit}`
				: '-',
		])
	);

	const formatClassHeight = det => {
		if (det.mean_height == null) return '-';
		const [num, unit] = formatLength(det.mean_height);
		return `${num} ${unit}`;
	};
	const formatClassStem = (det, level) => {
		const value = det.estimated_stems?.[level];
		return value == null ? '-' : formatNumber(value, 0);
	};

	const metricRows = [
		{ key: 'count', metric: 'Count', ...perClass(k => formatNumber(detections[k].count ?? 0)) },
		{ key: 'area', metric: 'Area', ...perClass(k => areaByKey[k]) },
		{
			key: 'height',
			metric: 'Mean height',
			...perClass(k => formatClassHeight(detections[k])),
		},
	];

	// Per-class stem rows — only when some class actually carries stem data.
	if (classKeys.some(k => detections[k]?.estimated_stems)) {
		for (const { key: level, label } of STEM_ESTIMATE_LEVELS) {
			metricRows.push({
				key: `stems_${level}`,
				metric: label,
				...perClass(k => formatClassStem(detections[k], level)),
			});
		}
	}

	// The metric-label column is pinned so it stays visible while the class
	// columns scroll horizontally (kicks in once the classes overflow the
	// sidebar width, via scroll.x on the Table).
	// Widths chosen so two classes fit the ~328px sidebar without a horizontal
	// scroll (150 + 2×85 = 320); a third class and beyond overflow into the
	// Table's scroll.x. The label column is pinned so it stays put while scrolling.
	const classColumns = [
		{ title: '', dataIndex: 'metric', key: 'metric', fixed: 'left', width: 150 },
		...classKeys.map(key => ({
			title: objectLabel(objects, key),
			dataIndex: key,
			key,
			align: 'right',
			width: 85,
		})),
	];

	return (
		<Flex vertical gap={12}>
			<Descriptions size="small" column={1} colon items={topItems} />

			{showStems && (
				<>
					<Divider style={{ margin: 0 }} />
					<Flex vertical gap={4}>
						<Text strong style={{ fontSize: '0.8rem' }}>
							Stem Estimates
						</Text>
						{stemRows.map(row => (
							<Flex key={row.key} justify="space-between" gap={8}>
								<Text type="secondary" style={{ fontSize: '0.8rem' }}>
									{row.label}
								</Text>
								<Text style={{ fontSize: '0.8rem' }}>{row.value}</Text>
							</Flex>
						))}
					</Flex>
				</>
			)}

			{classKeys.length > 0 && (
				<>
					<Divider style={{ margin: 0 }} />
					<CompactTable
						size="small"
						columns={classColumns}
						dataSource={metricRows}
						pagination={false}
						scroll={{ x: 'max-content' }}
					/>
				</>
			)}
		</Flex>
	);
};

export default HexagonStats;
